import React, { useState } from 'react';
import axios from 'axios';

import CardDialog from '~/components/CardDialog';
import MenuItem from '~/components/core/Atomic/MenuItem';
import SwitchPanel from '~/components/core/ConfigPanel/SwitchPanel';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import TextField from '~/components/core/Molecules/Fields/TextField';
import { reportAxiosError } from '~/Utils';

import copies from '../copies';

import ClaimActionGuardrailsSection from './ClaimActionGuardrailsSection';
import { EXECUTION_MODE_OPTIONS } from './executionMode';
import type { ClaimActionExecutionMode, EffectiveClaimActionConfig, ScopeRow } from './types';

interface EditClaimActionDialogProps {
  action: EffectiveClaimActionConfig;
  scope: ScopeRow;
  baseUrl: string;
  onClose: () => void;
  onSaved: () => void;
}

interface ActionFormState {
  /** The scope's own addition to the action's description; '' means it adds nothing. */
  instructions: string;
  enabled: boolean;
  executionMode: ClaimActionExecutionMode;
  /** Held as the string the input carries; parsed once, on save. */
  confidenceThreshold: string;
}

const toFormState = (action: EffectiveClaimActionConfig): ActionFormState => ({
  instructions: action.instructions ?? '',
  enabled: action.enabled,
  executionMode: action.execution_mode,
  confidenceThreshold: action.confidence_threshold === null ? '' : String(action.confidence_threshold ?? ''),
});

const EditClaimActionDialog: React.FC<EditClaimActionDialogProps> = ({ action, scope, baseUrl, onClose, onSaved }) => {
  const [form, setForm] = useState<ActionFormState>(() => toFormState(action));
  const [isSaving, setIsSaving] = useState(false);
  // A guardrail rule is written on its own request from the section below, which `isSaving` knows
  // nothing about. Hold the dialog — close included — while that request is outstanding, so a close
  // cannot land between the write and the list reload and leave the admin with no sign it happened.
  // Guardrails already written stay written — which is what the secondary action below is labelled
  // Close for; the hold here is about the window in which the write has not landed yet.
  const [isGuardrailProcessing, setIsGuardrailProcessing] = useState(false);
  const isBusy = isSaving || isGuardrailProcessing;

  const patchForm = (patch: Partial<ActionFormState>) => setForm((prev) => ({ ...prev, ...patch }));

  const isThresholdMode = form.executionMode === 'above_threshold';

  const isThresholdBlank = form.confidenceThreshold === '';

  const isFormInvalid = (() => {
    if (!isThresholdMode) {
      return false;
    }
    const value = Number(form.confidenceThreshold);
    return isThresholdBlank || isNaN(value) || value < 0 || value > 1;
  })();

  // Blank is invalid — it blocks Save — but it is not *wrong yet*, so it reads as the field's
  // standing instruction rather than as an error. Switching the mode blanks the field
  // (`handleModeChange`), and turning it red the instant an operator picks `above_threshold`, before
  // they have typed anything, would be scolding them for the state the dialog just put them in. A
  // value that is present and out of range is a mistake they made, and says so.
  const hasThresholdError = isFormInvalid && !isThresholdBlank;

  const canSave = !isBusy && !isFormInvalid;

  // Changing the mode drops any threshold typed under the previous one, so the value cannot come
  // back on a later switch and cannot be read as valid while the field is off screen.
  const handleModeChange = (executionMode: ClaimActionExecutionMode) =>
    patchForm({ executionMode, confidenceThreshold: '' });

  const handleSave = async () => {
    if (!canSave) return;
    setIsSaving(true);
    try {
      // Per-action PUT (key in the URL): upserts just this action's override row. The threshold is
      // sent as an explicit null off `above_threshold` rather than omitted, so a stored one is
      // cleared — the backend validator and the column's CheckConstraint both require that pairing.
      await axios.put(`${baseUrl}/${action.key}`, {
        sub_organization_id: scope.sub_organization_id,
        lob: scope.lob,
        // Sent as an explicit null when the operator leaves the field empty, so clearing the
        // instructions clears the stored ones rather than saving a blank string the backend
        // would then have to treat as "none" on every read.
        instructions: form.instructions.trim() || null,
        enabled: form.enabled,
        execution_mode: form.executionMode,
        confidence_threshold: isThresholdMode ? Number(form.confidenceThreshold) : null,
      });
      onSaved();
      onClose();
    } catch (error) {
      reportAxiosError(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <CardDialog
      isDialog
      open
      maxWidth="md"
      fullWidth
      title={`Edit ${action.display_name}`}
      onClose={onClose}
      preventClose={isBusy}
      footerActions={
        <DialogFooterActions
          onClickPrimary={handleSave}
          onClickSecondary={onClose}
          // "Close", not "Cancel": the Guardrails section below writes through its own endpoints
          // the moment a rule is saved or deleted, so leaving the dialog cannot undo that. Only the
          // fields above are held until Save. "Cancel" would promise a rollback of work already
          // persisted.
          secondaryLabel="Close"
          disabled={isBusy}
          primaryDisabled={!canSave}
        />
      }
    >
      <div className="flex flex-col gap-12">
        <TextField
          id={`claim-action-instructions-${action.key}`}
          label={copies.actions.instructionsColumn}
          fullWidth
          value={form.instructions}
          onChange={(value: string) => patchForm({ instructions: value })}
          helperText={copies.actions.instructionsHelper}
        />
        <SwitchPanel
          checked={form.enabled}
          label="Enabled"
          onChange={(_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => patchForm({ enabled: checked })}
        />
        <SingleSelectField<ClaimActionExecutionMode>
          id={`claim-action-execution-mode-${action.key}`}
          label={copies.actions.automaticExecution}
          value={form.executionMode}
          fullWidthField
          onChange={handleModeChange}
        >
          {EXECUTION_MODE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </SingleSelectField>
        {isThresholdMode ? (
          <TextField
            id={`claim-action-confidence-${action.key}`}
            label="Confidence"
            type="number"
            value={form.confidenceThreshold}
            onChange={(value: string) => patchForm({ confidenceThreshold: value })}
            error={hasThresholdError}
            // The range is stated whether or not the value is wrong: Save is disabled on a blank
            // field too, and without this the operator is left with a dead button and no rule.
            helperText={hasThresholdError ? copies.actions.thresholdError : copies.actions.thresholdHelper}
          />
        ) : null}
        <ClaimActionGuardrailsSection
          guardrailEventKeys={action.guardrail_event_keys}
          guardrailsRequiredConditions={action.guardrails_required_conditions}
          actionDisplayName={action.display_name}
          scope={scope}
          onProcessingChange={setIsGuardrailProcessing}
        />
      </div>
    </CardDialog>
  );
};

export default EditClaimActionDialog;
